import * as React from "react";
import { notification } from "antd";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { homeLink, linksOf, request, resourceLink } from "../../../services/cpln";
import { CreateNGAdvanced } from "./create_ng_advanced";
import { CreateNGBasic } from "./create_ng_basic";
import { FormDebug } from "./formDebug";
import { CreateNGTrackGVCAlias } from "./create_ng_trackGvcAlias";
import { Loader } from "../../../components/layout/loader";
import { Domain } from "../../../schema/types/domain/entity";
import { DomainInput } from "./types/input";
import { inputToDomain } from "./utils/utils";

interface NewNavItemBase {
  title: string;
  url?: string;
  onAdd?: (() => Promise<void>) | (() => void);
  onRemove?: (() => Promise<void>) | (() => void);
  info?: React.ReactNode;
  level?: 0 | 1 | 2;
  disabled?: boolean;
}

export interface NewNavItem extends NewNavItemBase {
  children?: NewNavItemBase[];
}

interface Props {
  domain?: Domain;
  initialValues: DomainInput;
  fetchItem?: any;
}
const DomainCreateNGRaw: React.FC<Props> = ({ domain, initialValues, fetchItem }) => {
  const navigate = useNavigate();

  const methods = useForm<DomainInput>({
    defaultValues: initialValues,
  });

  const { handleSubmit, watch, setValue } = methods;

  const [opacity, setOpacity] = React.useState(0);

  const mode = watch("mode") || "basic";
  const isEdit = watch("isEdit");

  React.useEffect(() => {
    setTimeout(() => setOpacity(1), 600);
  }, []);

  async function onConfirm(values: DomainInput) {
    try {
      setValue("isLoading", true);

      const body = inputToDomain(values);

      const method = isEdit ? "patch" : "post";
      const url = isEdit ? linksOf(domain!).self! : homeLink("domain");

      await request({ method, url, body });
      const itemLink = resourceLink("domain", body.name);
      setValue("isLoading", false);

      notification.success({
        message: "Success",
        description: isEdit ? "Domain is updated" : "Domain is created",
      });
      if (isEdit && fetchItem) {
        fetchItem();
      } else {
        setTimeout(() => {
          navigate(`/console${itemLink}`);
        }, 100);
      }
    } catch (e) {
      setValue("isLoading", false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      // TODO update these messages
      if (errorMessage.includes("need to prove ownership of the domain")) {
        errorMessage +=
          ". If you have already made the change to DNS, please wait a few minutes. It takes time for DNS propagation. Try again soon.";
      }
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  return (
    <>
      {opacity < 1 ? <Loader className="absolute" style={{ top: 200 }} reason="domain is being set up" /> : null}
      <div style={{ opacity, transition: "all 0.1s" }}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onConfirm)}>
            {mode === "advanced" ? <CreateNGAdvanced domain={domain} /> : <CreateNGBasic domain={domain} />}
            <CreateNGTrackGVCAlias />
            {/* <FormDebug /> */}
          </form>
        </FormProvider>
      </div>
    </>
  );
};

export const DomainCreateNG = observer(DomainCreateNGRaw);
